import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";
import { getFocusable } from "./focus";
import { IconButton } from "./IconButton";
import { IconClose } from "./icons";
import "./Modal.css";

export interface ModalProps {
  open: boolean;
  /** Called on Esc, close button and (by default) backdrop click. */
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Buttons row; put the primary action last. */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  /** Element to focus on open; defaults to the first focusable inside the body. */
  initialFocusRef?: RefObject<HTMLElement>;
  closeOnBackdrop?: boolean;
  /** Hide the × button (ConfirmDialog uses explicit buttons). */
  hideClose?: boolean;
  /** "alertdialog" for confirmations. */
  role?: "dialog" | "alertdialog";
  className?: string;
}

// Stack of open modals so Esc and the focus trap only act on the top-most one.
const stack: symbol[] = [];
let scrollLocks = 0;
let savedOverflow = "";

function lockScroll() {
  if (scrollLocks++ === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
}

function unlockScroll() {
  if (--scrollLocks === 0) document.body.style.overflow = savedOverflow;
}

/** Accessible modal dialog: portal, focus trap, Esc closes, focus restored on close. */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  initialFocusRef,
  closeOnBackdrop = true,
  hideClose = false,
  role = "dialog",
  className,
}: ModalProps) {
  const auto = useId();
  const titleId = `modal${auto}-title`;
  const descId = description ? `modal${auto}-desc` : undefined;
  const dialogRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const token = useRef(Symbol("modal"));
  const pressStartedOnBackdrop = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const me = token.current;
    const previous = document.activeElement as HTMLElement | null;
    stack.push(me);
    lockScroll();

    const frame = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const target =
        initialFocusRef?.current ??
        (bodyRef.current ? getFocusable(bodyRef.current)[0] : undefined) ??
        getFocusable(dialog)[0] ??
        dialog;
      target.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      const i = stack.lastIndexOf(me);
      if (i >= 0) stack.splice(i, 1);
      unlockScroll();
      if (previous && document.contains(previous)) previous.focus();
    };
    // initialFocusRef is a ref object; reading .current at open time is intended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const isTop = () => stack[stack.length - 1] === token.current;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!isTop()) return;
    if (e.key === "Escape") {
      e.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (e.key !== "Tab" || !dialogRef.current) return;
    const items = getFocusable(dialogRef.current);
    if (items.length === 0) {
      e.preventDefault();
      dialogRef.current.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialogRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const onBackdropDown = (e: MouseEvent<HTMLDivElement>) => {
    pressStartedOnBackdrop.current = e.target === e.currentTarget;
  };
  const onBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdrop && pressStartedOnBackdrop.current && e.target === e.currentTarget) {
      onCloseRef.current();
    }
    pressStartedOnBackdrop.current = false;
  };

  return createPortal(
    <div className="ui-modal-backdrop" onMouseDown={onBackdropDown} onClick={onBackdropClick}>
      <div
        ref={dialogRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        className={cx("ui-modal", `ui-modal--${size}`, className)}
        onKeyDown={handleKeyDown}
      >
        <header className="ui-modal__header">
          <div className="ui-modal__titles">
            <h2 className="ui-modal__title" id={titleId}>
              {title}
            </h2>
            {description ? (
              <p className="ui-modal__description" id={descId}>
                {description}
              </p>
            ) : null}
          </div>
          {hideClose ? null : (
            <IconButton size="sm" label="Close dialog" icon={<IconClose />} onClick={onClose} />
          )}
        </header>
        {children ? (
          <div className="ui-modal__body" ref={bodyRef}>
            {children}
          </div>
        ) : null}
        {footer ? <footer className="ui-modal__footer">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
